// Symmetry chart — the SYM surface (top of /supply and /demand).
//
// Question: "Where is earn matched to spend — and where do they diverge?"
//
// The economy is healthy when each category sees earn ≈ spend. Single-leg
// loops accumulate liability (if earn > spend) or starve demand (if spend
// > earn). Today: signup_bonus is 98% of earn, fee-defense is 73% of spend
// — the most asymmetric category pair in the system.
//
// Per row: earn share % vs spend share % (today and target), with the
// imbalance highlighted. Click to commit a "land symmetry on X by Y"
// decision.
//
// Owner: Kabelo · Ikechukwu (ecosystem oversight).

import { useState } from "react";

import {
  type ActionRow,
  type ActionStatus,
  addAction,
  deleteAction,
  findRowCommitment,
  updateAction,
} from "../lib/actions";
import {
  CATEGORIES,
  FIT_COLOR,
  FIT_LABEL,
  SIGNUP_BONUS_SOURCE,
  deriveFit,
  type CategoryFit,
} from "../lib/categories";
import { DUE_BY_MAX_OFFSET_DAYS, fmtDueBy, isoInDays, isoToday } from "../lib/due-by";

type RowState = "proposed" | "agreed" | "flight" | "blocked" | "done" | "disagreed";

interface Row {
  code: string;
  label: string;
  earnTodayPct: number;
  spendTodayPct: number;
  earnTargetPct: number;
  spendTargetPct: number;
  asymmetryToday: number; // earn - spend (today)
  asymmetryTarget: number; // earn - spend (target)
  rationale: string;
  fit: CategoryFit | null;
  commitment?: ActionRow;
}

interface Props {
  kpiId: string;
  actions: ActionRow[];
  currentUser: string;
  onCommitmentChanged: () => Promise<void> | void;
}

function buildRows(kpiId: string, actions: ActionRow[]): Row[] {
  const rows: Row[] = [];

  // signup_bonus is earn-only — most asymmetric row in the system.
  rows.push({
    code: SIGNUP_BONUS_SOURCE.code,
    label: SIGNUP_BONUS_SOURCE.label,
    earnTodayPct: SIGNUP_BONUS_SOURCE.earnTodayPct,
    spendTodayPct: 0,
    earnTargetPct: SIGNUP_BONUS_SOURCE.earnTargetPct,
    spendTargetPct: 0,
    asymmetryToday: SIGNUP_BONUS_SOURCE.earnTodayPct,
    asymmetryTarget: SIGNUP_BONUS_SOURCE.earnTargetPct,
    rationale: "Earn-only source · no spend leg by definition. Capping earn weight is the only lever.",
    fit: null,
    commitment: findRowCommitment(actions, kpiId, SIGNUP_BONUS_SOURCE.code),
  });

  for (const cat of CATEGORIES) {
    const earnToday = cat.earnTodayPct ?? 0;
    const spendToday = cat.spendTodayPct ?? 0;
    const earnTarget = cat.intent?.earnTargetPct ?? 0;
    const spendTarget = cat.intent?.spendTargetPct ?? 0;
    if (earnToday === 0 && spendToday === 0 && earnTarget === 0 && spendTarget === 0) continue;
    rows.push({
      code: cat.code,
      label: cat.label,
      earnTodayPct: earnToday,
      spendTodayPct: spendToday,
      earnTargetPct: earnTarget,
      spendTargetPct: spendTarget,
      asymmetryToday: earnToday - spendToday,
      asymmetryTarget: earnTarget - spendTarget,
      rationale: cat.intent?.rationale ?? "No earn/spend target · held / sunset / pre-test.",
      fit: deriveFit(cat),
      commitment: findRowCommitment(actions, kpiId, cat.code),
    });
  }

  // Sort by absolute asymmetry at target (biggest gap = most attention)
  return rows.sort((a, b) => Math.abs(b.asymmetryTarget) - Math.abs(a.asymmetryTarget));
}

function rowState(c: ActionRow | undefined): RowState {
  if (!c) return "proposed";
  if (c.kind === "disagree") return "disagreed";
  return c.status === "doing" ? "flight" : c.status === "blocked" ? "blocked" : c.status === "watch" ? "agreed" : c.status === "done" ? "done" : "proposed";
}

const STATE_LABEL: Record<RowState, string> = { proposed: "PROPOSED", agreed: "AGREED", flight: "IN FLIGHT", blocked: "BLOCKED", done: "DONE", disagreed: "DISAGREED" };
const STATE_COLOR: Record<RowState, string> = { proposed: "var(--ink-faint)", agreed: "#4fb3ff", flight: "var(--purple)", blocked: "var(--coral)", done: "#5ec98c", disagreed: "var(--ink-faint)" };

export function SymmetryChart({ kpiId, actions, currentUser, onCommitmentChanged }: Props) {
  const rows = buildRows(kpiId, actions);
  const [openCode, setOpenCode] = useState<string | null>(null);

  // Top single-leg loop concentration (largest spend share OR earn share)
  const maxEarn = rows.reduce((m, r) => Math.max(m, r.earnTodayPct), 0);
  const maxSpend = rows.reduce((m, r) => Math.max(m, r.spendTodayPct), 0);
  const maxToday = Math.max(maxEarn, maxSpend);

  // Symmetric categories (within 5pp of each other) at target
  const symmetricCount = rows.filter((r) => Math.abs(r.asymmetryTarget) <= 5 && (r.earnTargetPct > 0 || r.spendTargetPct > 0)).length;
  const totalActiveRows = rows.filter((r) => r.earnTargetPct > 0 || r.spendTargetPct > 0).length;

  return (
    <div style={wrap}>
      <div style={head}>
        <div style={heading}>Earn vs. spend per category · where the loop closes (and where it doesn't)</div>
        <div style={subtitle}>
          A healthy coin economy has earn ≈ spend within each category. Big positive gaps (earn &gt; spend) accumulate liability · coins issued that customers can't spend. Big negative gaps (spend &gt; earn) starve demand · customers want to spend but can't earn fast enough. Today is brutally asymmetric: one category dominates spend, another dominates earn, and they don't touch.
        </div>
        <div style={hint}>Click a row to discuss, commit an owner, or disagree.</div>
      </div>

      <div style={heroRow}>
        <HeroTile
          label="Single-leg loop concentration"
          todayValue={`${maxToday.toFixed(0)}%`}
          todaySub="biggest single-channel share · today it's signup_bonus on earn (98%) + fee-defense on spend (73%)"
          targetValue="≤30%"
          targetSub="no single channel above 30% of either leg"
        />
        <HeroTile
          label="Categories with closed loops"
          todayValue="0"
          todaySub="no category has both meaningful earn and meaningful spend yet"
          targetValue={`${symmetricCount} / ${totalActiveRows}`}
          targetSub="earn ≈ spend (within 5pp) in 5+ must-win categories"
        />
      </div>

      <div style={tableWrap}>
        <div style={tableHead}>
          <span style={{ ...tableHeadCell, flex: 2.2, textAlign: "left" }}>Category</span>
          <span style={tableHeadCell}>Earn today</span>
          <span style={tableHeadCell}>Spend today</span>
          <span style={tableHeadCell}>Gap today</span>
          <span style={tableHeadCell}>Earn target</span>
          <span style={tableHeadCell}>Spend target</span>
          <span style={tableHeadCell}>Gap target</span>
          <span style={tableHeadCell}>Viability</span>
          <span style={{ ...tableHeadCell, flex: 1.6 }}>Status</span>
          <span style={{ ...tableHeadCell, flex: 1.2, textAlign: "left" }}>Owner</span>
          <span style={{ ...tableHeadCell, flex: 2.4, textAlign: "left" }}>Rationale</span>
          <span style={{ ...tableHeadCell, flex: 0.4 }} />
        </div>
        {rows.map((r) => (
          <RowItem key={r.code} row={r} kpiId={kpiId} isOpen={openCode === r.code} onToggle={() => setOpenCode((c) => (c === r.code ? null : r.code))} currentUser={currentUser} onCommitmentChanged={onCommitmentChanged} />
        ))}
      </div>
    </div>
  );
}

function HeroTile({ label, todayValue, todaySub, targetValue, targetSub }: { label: string; todayValue: string; todaySub: string; targetValue: string; targetSub: string }) {
  return (
    <div style={heroTile}>
      <div style={heroLabel}>{label}</div>
      <div style={heroBody}>
        <div style={heroSide}><div style={heroSideTag}>TODAY</div><div style={{ ...heroValue, color: "var(--coral)" }}>{todayValue}</div><div style={heroSub}>{todaySub}</div></div>
        <div style={heroArrow}>→</div>
        <div style={heroSide}><div style={heroSideTag}>TARGET</div><div style={{ ...heroValue, color: "#5ec98c" }}>{targetValue}</div><div style={heroSub}>{targetSub}</div></div>
      </div>
    </div>
  );
}

function asymmetryColor(gap: number): string {
  const abs = Math.abs(gap);
  if (abs <= 5) return "#5ec98c";
  if (abs <= 15) return "var(--amber)";
  return "var(--coral)";
}
function gapText(gap: number): string {
  if (Math.abs(gap) < 0.5) return "—";
  return (gap > 0 ? "+" : "") + gap.toFixed(0) + "pp";
}

function RowItem({ row, kpiId, isOpen, onToggle, currentUser, onCommitmentChanged }: { row: Row; kpiId: string; isOpen: boolean; onToggle: () => void; currentUser: string; onCommitmentChanged: () => Promise<void> | void }) {
  const state = rowState(row.commitment);
  const disagreed = state === "disagreed";
  const [hover, setHover] = useState(false);
  const hasBlocker = !!row.commitment?.blockers?.trim();
  const todayGapColor = asymmetryColor(row.asymmetryToday);
  const targetGapColor = asymmetryColor(row.asymmetryTarget);

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <button
        type="button"
        onClick={onToggle}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        title={state === "proposed" ? "Click to discuss / commit / disagree" : "Click to edit commitment"}
        style={{ ...tableRow, textAlign: "left", background: isOpen ? "var(--bg-1, rgba(255,255,255,0.06))" : hover ? "rgba(83, 74, 183, 0.08)" : "transparent", opacity: disagreed ? 0.55 : 1, cursor: "pointer", width: "100%", border: "none", borderLeft: `3px solid ${isOpen ? "var(--purple)" : "transparent"}`, color: "inherit", transition: "background 120ms ease" }}
      >
        <span style={{ ...tableCell, flex: 2.2, display: "flex", alignItems: "center", gap: 8, textAlign: "left" }}>
          <span style={{ fontWeight: 600, color: "var(--ink)", textDecoration: disagreed ? "line-through" : "none" }}>{row.label}</span>
        </span>
        <span style={tableCell}>{row.earnTodayPct.toFixed(1)}%</span>
        <span style={tableCell}>{row.spendTodayPct.toFixed(1)}%</span>
        <span style={{ ...tableCell, color: todayGapColor, fontWeight: 700 }}>{gapText(row.asymmetryToday)}</span>
        <span style={tableCell}>{row.earnTargetPct.toFixed(0)}%</span>
        <span style={tableCell}>{row.spendTargetPct.toFixed(0)}%</span>
        <span style={{ ...tableCell, color: targetGapColor, fontWeight: 700 }}>{gapText(row.asymmetryTarget)}</span>
        <span style={tableCell}>{row.fit && <span style={fitChip(row.fit)}>{FIT_LABEL[row.fit]}</span>}</span>
        <span style={{ ...tableCell, flex: 1.6, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3 }}>
          <span style={stateChip(state)}>{STATE_LABEL[state]}</span>
          {row.commitment?.dueBy && (
            <span style={dueChip(row.commitment.dueBy)} title={`Due ${fmtDueBy(row.commitment.dueBy)}`}>⏱ due {fmtDueBy(row.commitment.dueBy)}</span>
          )}
          {hasBlocker && <span style={blockerChip} title={row.commitment!.blockers}>✕ {row.commitment!.blockers}</span>}
        </span>
        <span style={{ ...tableCell, flex: 1.2, textAlign: "left", color: row.commitment?.owner ? "var(--ink)" : "var(--ink-faint)", fontWeight: row.commitment?.owner ? 600 : 400 }}>
          {row.commitment?.owner || "—"}
        </span>
        <span style={{ ...tableCell, flex: 2.4, textAlign: "left", color: "var(--ink-dim)", fontSize: 11.5, lineHeight: 1.5 }}>{row.rationale}</span>
        <span style={{ ...tableCell, flex: 0.4, textAlign: "center" }}><span style={chevron(isOpen, state === "proposed" && hover)}>{isOpen ? "▾" : "▸"}</span></span>
      </button>

      {isOpen && (
        <RowEditor row={row} kpiId={kpiId} currentUser={currentUser} onClose={onToggle} onCommitmentChanged={onCommitmentChanged} />
      )}
    </div>
  );
}

function RowEditor({ row, kpiId, currentUser, onClose, onCommitmentChanged }: { row: Row; kpiId: string; currentUser: string; onClose: () => void; onCommitmentChanged: () => Promise<void> | void }) {
  const existing = row.commitment;
  const [owner, setOwner] = useState(existing?.owner ?? "");
  const [doing, setDoing] = useState(existing?.doing ?? "");
  const [blockers, setBlockers] = useState(existing?.blockers ?? "");
  const [dueBy, setDueBy] = useState(existing?.dueBy ?? "");
  const [status, setStatus] = useState<ActionStatus>(existing?.status ?? "watch");
  const [saving, setSaving] = useState(false);
  const focusPath = `${kpiId}:${row.code}`;
  const minDate = isoToday();
  const maxDate = isoInDays(DUE_BY_MAX_OFFSET_DAYS);
  const title = `${row.label} · land earn/spend symmetry (gap ${gapText(row.asymmetryTarget)} at target)`;

  async function save(kind: "commitment" | "disagree") {
    setSaving(true);
    try {
      if (existing) await updateAction(existing.id, { kind, owner, doing, blockers,
          dueBy, status: kind === "disagree" ? "watch" : status, title: kind === "disagree" ? `Disagree · ${row.label}` : title });
      else await addAction({ title: kind === "disagree" ? `Disagree · ${row.label}` : title, impacts: [focusPath], owner, status: kind === "disagree" ? "watch" : status, blockers, doing, dueBy, author: currentUser, kind });
      await onCommitmentChanged();
      onClose();
    } finally { setSaving(false); }
  }
  async function clearCommitment() {
    if (!existing) { onClose(); return; }
    setSaving(true);
    try { await deleteAction(existing.id); await onCommitmentChanged(); onClose(); } finally { setSaving(false); }
  }

  return (
    <div style={editorWrap}>
      <div style={editorHead}>
        <span style={editorEyebrow}>{existing ? "EDIT" : "PROPOSE"} · {row.label} · earn {row.earnTargetPct.toFixed(0)}% / spend {row.spendTargetPct.toFixed(0)}% (gap {gapText(row.asymmetryTarget)})</span>
        <span style={editorRationale}>{row.rationale}</span>
      </div>
      <div style={editorGrid}>
        <label style={editorField}><span style={editorLabel}>Status</span>
          <select value={status} onChange={(e) => setStatus(e.target.value as ActionStatus)} style={editorInput}>
            <option value="watch">Agreed · not yet started</option>
            <option value="doing">In flight</option>
            <option value="blocked">Blocked</option>
            <option value="done">Done · symmetry reached</option>
          </select>
        </label>
        <label style={editorField}><span style={editorLabel}>Owner</span><input value={owner} onChange={(e) => setOwner(e.target.value)} placeholder="Who is committed?" style={editorInput} /></label>
        <label style={editorField}><span style={editorLabel}>By when (max 30 days)</span>
          <input type="date" value={dueBy} min={minDate} max={maxDate} onChange={(e) => setDueBy(e.target.value)} style={editorInput} />
        </label>
        <label style={editorField}><span style={editorLabel}>By when (max 30 days)</span>
          <input type="date" value={dueBy} min={minDate} max={maxDate} onChange={(e) => setDueBy(e.target.value)} style={editorInput} />
        </label>
        <label style={{ ...editorField, gridColumn: "span 2" }}><span style={editorLabel}>Decision / what we're doing</span>
          <input value={doing} onChange={(e) => setDoing(e.target.value)} placeholder="e.g. 'Close the gap on Mobile by building earn-mint to match the 28% spend share'" style={editorInput} />
        </label>
        <label style={{ ...editorField, gridColumn: "span 2" }}><span style={editorLabel}>Blocker (if any)</span><input value={blockers} onChange={(e) => setBlockers(e.target.value)} placeholder="What's in the way?" style={editorInput} /></label>
      </div>
      <div style={editorActions}>
        <button type="button" onClick={() => save("commitment")} disabled={saving || !owner.trim()} style={btnPrimary}>{existing && existing.kind !== "disagree" ? "Update commitment" : "Commit"}</button>
        <button type="button" onClick={() => save("disagree")} disabled={saving} style={btnGhost}>{existing?.kind === "disagree" ? "Update disagree" : "Disagree"}</button>
        {existing && <button type="button" onClick={clearCommitment} disabled={saving} style={btnDanger}>Clear</button>}
        <span style={{ flex: 1 }} />
        <button type="button" onClick={onClose} disabled={saving} style={btnGhost}>Close</button>
      </div>
    </div>
  );
}

// styles
const wrap: React.CSSProperties = { padding: "16px 18px", borderTop: "1px solid var(--edge)", borderBottom: "1px solid var(--edge)", background: "var(--panel-1, rgba(255,255,255,0.02))", display: "flex", flexDirection: "column", gap: 12 };
const head: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 4 };
const heading: React.CSSProperties = { fontSize: 13, fontWeight: 700, color: "var(--ink)" };
const subtitle: React.CSSProperties = { fontSize: 11.5, color: "var(--ink-faint)", lineHeight: 1.5 };
const hint: React.CSSProperties = { fontFamily: "var(--font-mono)", fontSize: 9.5, letterSpacing: "0.1em", color: "var(--purple)", fontWeight: 700, textTransform: "uppercase", marginTop: 4 };
const heroRow: React.CSSProperties = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 8 };
const heroTile: React.CSSProperties = { border: "1px solid var(--edge)", borderRadius: 4, padding: "12px 14px", background: "var(--bg-1, rgba(255,255,255,0.03))" };
const heroLabel: React.CSSProperties = { fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.1em", color: "var(--ink-faint)", fontWeight: 700, textTransform: "uppercase", marginBottom: 10 };
const heroBody: React.CSSProperties = { display: "flex", alignItems: "center", gap: 16 };
const heroSide: React.CSSProperties = { flex: 1, display: "flex", flexDirection: "column", gap: 3 };
const heroSideTag: React.CSSProperties = { fontFamily: "var(--font-mono)", fontSize: 9, letterSpacing: "0.12em", color: "var(--ink-faint)", fontWeight: 700 };
const heroValue: React.CSSProperties = { fontSize: 28, fontWeight: 800, lineHeight: 1, fontVariantNumeric: "tabular-nums" };
const heroSub: React.CSSProperties = { fontSize: 11, color: "var(--ink-dim)", lineHeight: 1.4, marginTop: 2 };
const heroArrow: React.CSSProperties = { fontSize: 22, color: "var(--ink-faint)", flexShrink: 0 };
const tableWrap: React.CSSProperties = { marginTop: 8, display: "flex", flexDirection: "column", border: "1px solid var(--edge)", borderRadius: 4 };
const tableHead: React.CSSProperties = { display: "flex", gap: 8, padding: "8px 10px", borderBottom: "1px solid var(--edge)", background: "var(--bg-1, rgba(255,255,255,0.03))" };
const tableHeadCell: React.CSSProperties = { flex: 1, fontFamily: "var(--font-mono)", fontSize: 9, letterSpacing: "0.08em", color: "var(--ink-faint)", fontWeight: 700, textTransform: "uppercase", textAlign: "right" };
const tableRow: React.CSSProperties = { display: "flex", gap: 8, padding: "9px 10px", borderBottom: "1px solid var(--edge)", alignItems: "center" };
const tableCell: React.CSSProperties = { flex: 1, fontSize: 11.5, color: "var(--ink-dim)", fontVariantNumeric: "tabular-nums", textAlign: "right" };
function fitChip(fit: CategoryFit): React.CSSProperties { const color = FIT_COLOR[fit]; return { display: "inline-block", fontFamily: "var(--font-mono)", fontSize: 8.5, fontWeight: 700, letterSpacing: "0.08em", color, border: `1px solid ${color}`, borderRadius: 3, padding: "1px 5px" }; }
function stateChip(state: RowState): React.CSSProperties { const color = STATE_COLOR[state]; return { display: "inline-block", fontFamily: "var(--font-mono)", fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", color, background: `${color}22`, border: `1px solid ${color}66`, padding: "2px 7px", borderRadius: 3, whiteSpace: "nowrap" }; }
const blockerChip: React.CSSProperties = { display: "inline-block", maxWidth: 220, fontSize: 10, color: "var(--coral)", background: "rgba(196, 74, 74, 0.12)", border: "1px solid rgba(196, 74, 74, 0.4)", padding: "1px 6px", borderRadius: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontStyle: "italic" };
function chevron(isOpen: boolean, emphasised: boolean): React.CSSProperties { return { display: "inline-block", fontSize: 13, color: isOpen ? "var(--purple)" : emphasised ? "var(--purple)" : "var(--ink-faint)", fontWeight: 700, transition: "transform 120ms ease, color 120ms ease" }; }
const editorWrap: React.CSSProperties = { padding: "14px 16px 16px", background: "var(--bg-1, rgba(255,255,255,0.04))", borderBottom: "1px solid var(--edge)", display: "flex", flexDirection: "column", gap: 12 };
const editorHead: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 4 };
const editorEyebrow: React.CSSProperties = { fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.12em", color: "var(--ink-faint)", fontWeight: 700 };
const editorRationale: React.CSSProperties = { fontSize: 11.5, color: "var(--ink-dim)", fontStyle: "italic" };
const editorGrid: React.CSSProperties = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 };
const editorField: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 4 };
const editorLabel: React.CSSProperties = { fontFamily: "var(--font-mono)", fontSize: 9, letterSpacing: "0.1em", color: "var(--ink-faint)", fontWeight: 700, textTransform: "uppercase" };
const editorInput: React.CSSProperties = { background: "var(--panel-2)", border: "1px solid var(--edge)", borderRadius: 3, padding: "6px 8px", color: "var(--ink)", fontSize: 12, fontFamily: "inherit" };
const editorActions: React.CSSProperties = { display: "flex", gap: 8, alignItems: "center" };
const btnPrimary: React.CSSProperties = { fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.1em", fontWeight: 700, textTransform: "uppercase", padding: "6px 12px", borderRadius: 3, border: "1px solid var(--purple)", background: "var(--purple)", color: "white", cursor: "pointer" };
const btnGhost: React.CSSProperties = { fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.1em", fontWeight: 700, textTransform: "uppercase", padding: "6px 12px", borderRadius: 3, border: "1px solid var(--edge)", background: "transparent", color: "var(--ink-dim)", cursor: "pointer" };
const btnDanger: React.CSSProperties = { fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.1em", fontWeight: 700, textTransform: "uppercase", padding: "6px 12px", borderRadius: 3, border: "1px solid var(--coral)", background: "transparent", color: "var(--coral)", cursor: "pointer" };

function dueChip(iso: string): React.CSSProperties {
  const tone = (() => {
    const d = new Date(iso + "T00:00:00").getTime();
    const today = new Date(new Date().toISOString().slice(0, 10) + "T00:00:00").getTime();
    const days = Math.round((d - today) / 86_400_000);
    if (days < 0) return "var(--coral)";
    if (days <= 7) return "var(--amber)";
    return "#4fb3ff";
  })();
  return {
    display: "inline-block", fontFamily: "var(--font-mono)", fontSize: 9,
    fontWeight: 700, letterSpacing: "0.08em", color: tone,
    background: `${tone}22`, border: `1px solid ${tone}66`,
    padding: "1px 6px", borderRadius: 3, whiteSpace: "nowrap",
  };
}

